"use client";

import { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Server } from "lucide-react";
import { ProviderSummary, Model, SortOption } from "@/types/model";
import { filterAndSortModels } from "@/lib/api/models";
import { ModelCard } from "@/components/models/ModelCard";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Empty,
  EmptyMedia,
  EmptyHeader,
  EmptyDescription,
} from "@/components/ui/empty";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Ghost } from "lucide-react";
import { formatRegionLabel } from "@/lib/utils";

interface ProviderDetailContentProps {
  provider: ProviderSummary;
  models: Model[];
}

export function ProviderDetailContent({
  provider,
  models,
}: ProviderDetailContentProps) {
  const t = useTranslations("providerDetail");
  const tModels = useTranslations("models");
  const [sort, setSort] = useState<SortOption>("newest");

  const sortedModels = useMemo(
    () => filterAndSortModels(models, { sort }),
    [models, sort],
  );

  const regionLabel = provider.region
    ? formatRegionLabel(provider.region)
    : null;

  return (
    <main className="flex-1">
      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* 供应商头部：图标 + 名称 + 描述 */}
        <header className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
          <Avatar className="h-20 w-20 shrink-0 rounded-xl border-2 border-border bg-muted/50 sm:h-24 sm:w-24">
            <AvatarImage
              src={provider.logo}
              alt={provider.name}
              className="rounded-xl"
            />
            <AvatarFallback className="rounded-xl bg-primary/10 text-2xl text-primary">
              <Server className="h-10 w-10" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {provider.name}
            </h1>
            {provider.description && (
              <p className="mt-2 text-muted-foreground whitespace-pre-wrap break-all">
                {provider.description}
              </p>
            )}
            {regionLabel && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="secondary"
                        className="text-xs font-normal"
                      >
                        {regionLabel}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{regionLabel}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </header>

        {/* 模型列表区域：排序 + 数量 + 网格 */}
        <section>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">
              {t("modelsCount", { count: sortedModels.length })}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t("sortLabel")}
              </span>
              <Select
                value={sort}
                onValueChange={(v) => setSort(v as SortOption)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  <SelectItem value="newest">
                    {tModels("sort.newest")}
                  </SelectItem>
                  <SelectItem value="name">{tModels("sort.name")}</SelectItem>
                  <SelectItem value="price-low">
                    {tModels("sort.priceLow")}
                  </SelectItem>
                  <SelectItem value="price-high">
                    {tModels("sort.priceHigh")}
                  </SelectItem>
                  <SelectItem value="context-high">
                    {tModels("sort.contextHigh")}
                  </SelectItem>
                  <SelectItem value="latency-low">
                    {tModels("sort.latencyLow")}
                  </SelectItem>
                    <SelectItem value="throughput-high">
                      {tModels("sort.throughputHigh")}
                    </SelectItem>
                    <SelectItem value="uptime-high">
                      {tModels("sort.uptimeHigh")}
                    </SelectItem>
                  </SelectContent>
              </Select>
            </div>
          </div>

          {sortedModels.length > 0 ? (
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {sortedModels.map((model) => (
                <ModelCard key={model.id} model={model} />
              ))}
            </div>
          ) : (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Ghost />
                </EmptyMedia>
                <EmptyDescription>{t("noModels")}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </section>
      </div>
    </main>
  );
}
